import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  LogOut,
  User,
  BookOpen,
  Clock,
  DollarSign,
  Plus,
  Calendar as CalendarIcon,
  FileText,
  Download,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { format } from "date-fns";
import { ThemeToggle } from "../components/ThemeToggle";
import { useTheme } from "../context/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Theme-aware logos (SVG with transparent background)
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_glass-teach/artifacts/drr8t3sh_Dark_2.svg";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_glass-teach/artifacts/6ix3baet_Light_2.svg";

const HOURLY_RATE = 27.50;
const HALF_HOUR_RATE = 22.00;

export const InstructorDashboard = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const logoUrl = theme === 'dark' ? LOGO_DARK : LOGO_LIGHT;
  const [profile, setProfile] = useState(null);
  const [students, setStudents] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLogLesson, setShowLogLesson] = useState(false);

  const [lessonForm, setLessonForm] = useState({
    student_id: "",
    course: "",
    duration_type: "hourly", // "hourly" or "30min"
    duration_hours: 1,
    date: new Date(),
    notes: "",
  });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("mms_token")}` },
  });

  const fetchData = async () => {
    try {
      const [profileRes, studentsRes, lessonsRes] = await Promise.all([
        axios.get(`${API}/instructor/profile`, getAuthHeaders()),
        axios.get(`${API}/instructor/students`, getAuthHeaders()),
        axios.get(`${API}/instructor/lessons`, getAuthHeaders()),
      ]);
      setProfile(profileRes.data);
      setStudents(studentsRes.data);
      setLessons(lessonsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        localStorage.clear();
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const calculatePayment = () => {
    if (lessonForm.duration_type === "30min") {
      return HALF_HOUR_RATE;
    }
    return parseFloat(lessonForm.duration_hours) * HOURLY_RATE;
  };

  const handleLogLesson = async () => {
    if (!lessonForm.student_id || !lessonForm.course) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (lessonForm.duration_type === "hourly" && !lessonForm.duration_hours) {
      toast.error("Please specify duration hours");
      return;
    }

    try {
      await axios.post(
        `${API}/instructor/lessons`,
        {
          student_id: lessonForm.student_id,
          course: lessonForm.course,
          duration_type: lessonForm.duration_type,
          duration_hours: lessonForm.duration_type === "30min" ? 0.5 : parseFloat(lessonForm.duration_hours),
          date: format(lessonForm.date, "yyyy-MM-dd"),
          notes: lessonForm.notes,
        },
        getAuthHeaders()
      );
      toast.success("Lesson logged successfully!");
      setShowLogLesson(false);
      setLessonForm({
        student_id: "",
        course: "",
        duration_type: "hourly",
        duration_hours: 1,
        date: new Date(),
        notes: "",
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to log lesson");
    }
  };

  const pendingLessons = lessons.filter((l) => l.status === "pending");
  const recentLessons = lessons.slice(0, 5);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-zinc-950' : 'bg-slate-50'}`}>
        <div className={`w-8 h-8 border-2 rounded-full animate-spin ${theme === 'dark' ? 'border-white/20 border-t-white' : 'border-indigo-200 border-t-indigo-600'}`} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-zinc-950' : 'bg-slate-50'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 backdrop-blur-xl border-b ${theme === 'dark' ? 'bg-zinc-950/80 border-white/10' : 'bg-white/80 border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={logoUrl} alt="MMS Logo" className="h-12 w-auto object-contain logo-glass" />
            <span className={`text-lg font-semibold hidden sm:block ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>MMS Portal</span>
          </div>
          <nav className="flex items-center gap-2 sm:gap-4">
            <Link to="/dashboard" className={`glass-button-ghost text-sm ${theme === 'dark' ? 'text-white' : 'text-indigo-600'}`}>
              Dashboard
            </Link>
            <Link to="/profile" className="glass-button-ghost text-sm">
              Profile
            </Link>
            <Link to="/materials" className="glass-button-ghost text-sm">
              Materials
            </Link>
            <ThemeToggle />
            <button onClick={handleLogout} className="glass-button-ghost text-sm flex items-center gap-1">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <h1 className={`text-3xl sm:text-4xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
            Welcome, {profile?.first_name || profile?.full_legal_name?.split(" ")[0]}
          </h1>
          <p className={theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}>Here's your teaching overview</p>
        </motion.div>

        {/* Stats Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {/* Unpaid Hours Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card-hover p-6"
          >
            <Clock className={`w-8 h-8 mb-4 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
            <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Unpaid Hours</p>
            <p className={`text-3xl font-bold mono ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`} data-testid="unpaid-hours">
              {profile?.unpaid_hours?.toFixed(1) || "0.0"}
            </p>
          </motion.div>

          {/* Amount Due Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card-hover p-6 lg:col-span-2"
          >
            <DollarSign className={`w-8 h-8 mb-4 ${theme === 'dark' ? 'text-white' : 'text-indigo-600'}`} />
            <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Amount Due</p>
            <p className={`text-4xl font-bold mono ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`} data-testid="amount-due">
              ${profile?.amount_due?.toFixed(2) || "0.00"}
            </p>
            <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
              Rates: ${HOURLY_RATE}/hr or ${HALF_HOUR_RATE}/30min
            </p>
          </motion.div>

          {/* Total Lessons Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card-hover p-6"
          >
            <BookOpen className={`w-8 h-8 mb-4 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
            <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Total Lessons</p>
            <p className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`} data-testid="total-lessons">
              {lessons.length}
            </p>
          </motion.div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 mb-10">
          <Button
            data-testid="log-lesson-btn"
            onClick={() => setShowLogLesson(true)}
            className="glass-button-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Log Lesson
          </Button>
          <Link to="/profile">
            <Button className="glass-button-secondary flex items-center gap-2">
              <User className="w-5 h-5" />
              View Profile
            </Button>
          </Link>
          <Link to="/materials">
            <Button className="glass-button-secondary flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Course Materials
            </Button>
          </Link>
          <a
            href={`${API}/instructor/lessons/export`}
            onClick={(e) => {
              e.preventDefault();
              const token = localStorage.getItem("mms_token");
              fetch(`${API}/instructor/lessons/export`, {
                headers: { Authorization: `Bearer ${token}` }
              })
              .then(res => res.blob())
              .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'lesson_history.csv';
                a.click();
                window.URL.revokeObjectURL(url);
                toast.success("Lesson history exported!");
              })
              .catch(() => toast.error("Failed to export"));
            }}
            data-testid="export-lessons-btn"
          >
            <Button className="glass-button-secondary flex items-center gap-2">
              <Download className="w-5 h-5" />
              Export History
            </Button>
          </a>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Pending Lessons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-card p-6"
          >
            <h2 className={`text-xl font-semibold mb-6 flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
              <Clock className={`w-5 h-5 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
              Pending Payment ({pendingLessons.length})
            </h2>
            {pendingLessons.length === 0 ? (
              <p className={`text-center py-8 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>No pending lessons</p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {pendingLessons.map((lesson) => (
                  <div
                    key={lesson.id}
                    data-testid={`pending-lesson-${lesson.id}`}
                    className={`flex items-center justify-between p-4 rounded-xl border ${
                      theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-100'
                    }`}
                  >
                    <div>
                      <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>{lesson.student_name}</p>
                      <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                        {lesson.course} • {lesson.duration_type === "30min" ? "30 min" : `${lesson.duration_hours}h`}
                      </p>
                    </div>
                    <p className={`font-semibold mono ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                      ${lesson.payment_amount.toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-card p-6"
          >
            <h2 className={`text-xl font-semibold mb-6 flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
              <BookOpen className={`w-5 h-5 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
              Recent Lessons
            </h2>
            {recentLessons.length === 0 ? (
              <p className={`text-center py-8 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>No lessons logged yet</p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {recentLessons.map((lesson) => (
                  <div
                    key={lesson.id}
                    className={`flex items-center justify-between p-4 rounded-xl border ${
                      theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-100'
                    }`}
                  >
                    <div>
                      <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>{lesson.student_name}</p>
                      <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                        {lesson.course} • {lesson.date}
                      </p>
                    </div>
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        lesson.status === "paid"
                          ? "bg-green-500/20 text-green-400"
                          : "bg-yellow-500/20 text-yellow-400"
                      }`}
                    >
                      {lesson.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </main>

      {/* Log Lesson Modal */}
      {showLogLesson && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className={`absolute inset-0 backdrop-blur-sm ${theme === 'dark' ? 'bg-black/60' : 'bg-black/40'}`}
            onClick={() => setShowLogLesson(false)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative glass-card p-8 w-full max-w-lg"
          >
            <h2 className={`text-2xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>Log a Lesson</h2>

            <div className="space-y-5">
              {/* Student Select */}
              <div>
                <label className="form-label">Student</label>
                <Select
                  value={lessonForm.student_id}
                  onValueChange={(value) =>
                    setLessonForm((prev) => ({ ...prev, student_id: value }))
                  }
                >
                  <SelectTrigger data-testid="lesson-student-select" className="glass-input w-full">
                    <SelectValue placeholder="Select a student" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map((student) => (
                      <SelectItem key={student.id} value={student.id}>
                        {student.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Course Select */}
              <div>
                <label className="form-label">Course</label>
                <Select
                  value={lessonForm.course}
                  onValueChange={(value) =>
                    setLessonForm((prev) => ({ ...prev, course: value }))
                  }
                >
                  <SelectTrigger data-testid="lesson-course-select" className="glass-input w-full">
                    <SelectValue placeholder="Select a course" />
                  </SelectTrigger>
                  <SelectContent>
                    {profile?.authorized_courses?.map((course) => (
                      <SelectItem key={course} value={course}>
                        {course}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Duration Type */}
              <div>
                <label className="form-label">Lesson Type</label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <button
                    type="button"
                    data-testid="duration-30min-btn"
                    onClick={() => setLessonForm((prev) => ({ ...prev, duration_type: "30min" }))}
                    className={`p-4 rounded-xl border text-center transition-all ${
                      lessonForm.duration_type === "30min"
                        ? theme === 'dark'
                          ? "bg-white text-black border-white"
                          : "bg-indigo-600 text-white border-indigo-600"
                        : theme === 'dark'
                          ? "bg-white/5 text-white border-white/10 hover:border-white/20"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <p className="font-semibold">30 Minutes</p>
                    <p className={`text-sm mt-1 ${lessonForm.duration_type === "30min" ? "" : theme === 'dark' ? "text-slate-400" : "text-slate-500"}`}>
                      ${HALF_HOUR_RATE.toFixed(2)}
                    </p>
                  </button>
                  <button
                    type="button"
                    data-testid="duration-hourly-btn"
                    onClick={() => setLessonForm((prev) => ({ ...prev, duration_type: "hourly" }))}
                    className={`p-4 rounded-xl border text-center transition-all ${
                      lessonForm.duration_type === "hourly"
                        ? theme === 'dark'
                          ? "bg-white text-black border-white"
                          : "bg-indigo-600 text-white border-indigo-600"
                        : theme === 'dark'
                          ? "bg-white/5 text-white border-white/10 hover:border-white/20"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <p className="font-semibold">Hourly</p>
                    <p className={`text-sm mt-1 ${lessonForm.duration_type === "hourly" ? "" : theme === 'dark' ? "text-slate-400" : "text-slate-500"}`}>
                      ${HOURLY_RATE.toFixed(2)}/hr
                    </p>
                  </button>
                </div>
              </div>

              {/* Duration Hours (only for hourly) */}
              {lessonForm.duration_type === "hourly" && (
                <div>
                  <label className="form-label">Duration (Hours)</label>
                  <input
                    data-testid="lesson-duration-input"
                    type="number"
                    step="0.5"
                    min="0.5"
                    max="8"
                    value={lessonForm.duration_hours}
                    onChange={(e) =>
                      setLessonForm((prev) => ({ ...prev, duration_hours: e.target.value }))
                    }
                    className="glass-input w-full"
                  />
                </div>
              )}

              {/* Payment Preview */}
              <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-white/5' : 'bg-slate-100'}`}>
                <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Payment Amount</p>
                <p className={`text-2xl font-bold mono ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                  ${calculatePayment().toFixed(2)}
                </p>
              </div>

              {/* Date */}
              <div>
                <label className="form-label">Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      data-testid="lesson-date-picker"
                      variant="outline"
                      className="glass-input w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(lessonForm.date, "PPP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className={`w-auto p-0 ${theme === 'dark' ? 'bg-zinc-900 border-white/10' : 'bg-white border-slate-200'}`}>
                    <Calendar
                      mode="single"
                      selected={lessonForm.date}
                      onSelect={(date) =>
                        setLessonForm((prev) => ({ ...prev, date: date || new Date() }))
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Notes */}
              <div>
                <label className="form-label">Notes (Optional)</label>
                <textarea
                  data-testid="lesson-notes-input"
                  value={lessonForm.notes}
                  onChange={(e) =>
                    setLessonForm((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  className="glass-input w-full min-h-[80px] resize-none"
                  placeholder="Any notes about the lesson..."
                />
              </div>
            </div>

            <div className="flex gap-4 mt-8">
              <Button
                data-testid="cancel-lesson-btn"
                onClick={() => setShowLogLesson(false)}
                className="glass-button-secondary flex-1"
              >
                Cancel
              </Button>
              <Button
                data-testid="submit-lesson-btn"
                onClick={handleLogLesson}
                className="glass-button-primary flex-1"
              >
                Log Lesson
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};
